import {NoteModel, UserModel} from '../models';
import  normalize from 'json-api-normalize';
import { SavedSearchModel } from '../models/saved-search.model';
import { environment } from '../../environments/environment';
import { jwtDecode } from 'jwt-decode';
import { SearchServiceHelper } from './search-service.helper';
import {CommentModel} from "../models/note.model";

export class UserServiceHelper {
  private static DASHBOARD_PROGRAMS_FIELDS = [
    'field_dashboard_programs_ref.field_udp_dashboard_array.id',
    'field_dashboard_programs_ref.field_udp_dashboard_array.drupal_internal__nid',
    'field_dashboard_programs_ref.field_udp_dashboard_array.field_address',
    'field_dashboard_programs_ref.field_udp_dashboard_array.title',
    'field_dashboard_programs_ref.field_udp_dashboard_array.field_program_id',
    'field_dashboard_programs_ref.field_udp_dashboard_array.field_geolocation',
    'field_dashboard_programs_ref.field_user_notes',
    'field_dashboard_programs_ref.field_user_notes.id',
    'field_dashboard_programs_ref.field_user_notes.field_program_comments',
    'field_dashboard_programs_ref.field_user_notes.field_program_community',
    'field_dashboard_programs_ref.field_user_notes.field_program_fit',
    'field_dashboard_programs_ref.field_user_notes.field_fac_teaching_avail',
    'field_dashboard_programs_ref.field_user_notes.field_program_interview',
    'field_dashboard_programs_ref.field_user_notes.field_match_rank_order',
    'field_dashboard_programs_ref.field_user_notes.field_program.field_program_id',
    'field_dashboard_programs_ref.field_user_notes.field_program.id',
    'field_dashboard_programs_ref.field_user_notes.field_program.title',
    'field_dashboard_programs_ref.field_user_notes.field_program.field_specialty.title',
    'field_dashboard_programs_ref.field_user_notes.field_program_rating',
    'field_dashboard_programs_ref.field_user_notes.field_program_col',
    'field_dashboard_programs_ref.field_user_notes.field_program_morale',
    'field_dashboard_programs_ref.field_user_notes.field_program_status',
    'field_dashboard_programs_ref.field_udp_dashboard_array.field_program.field_program_id',
    'field_dashboard_programs_ref.field_udp_dashboard_array.field_program.specialty.title',
    'field_specialty.title',
  ];

  public static parseUsers(userJson: any): UserModel[] {
    const userFields = [
      'id',
      'mail',
      'name',
      'roles.label',
      'field_saved_searches',
      'field_program_download_count',
      'saml_tam_id',
      'saml_life_stage',
      'saml_member_status',
      'field_first_name',
      'field_last_name',
      'field_dashboard_programs_ref.field_udp_dashboard_array.field_specialty.title',
      'field_dashboard_programs_ref.field_udp_dashboard_array.field_specialty.drupal_internal__nid',
      'field_dashboard_programs_ref.field_udp_dashboard_array.field_specialty.field_specialty_designation',
      'field_dashboard_programs_ref.field_udp_dashboard_array.path'
    ].concat(UserServiceHelper.DASHBOARD_PROGRAMS_FIELDS);


    let normdUsers = normalize(userJson).get(userFields);
    if (!Array.isArray(normdUsers)) {
      normdUsers = [normdUsers];
    }

    return normdUsers.map(
      (user: any) =>
        new UserModel({
          uuid: user.id,
          udp_internal_id: this.getUdpInternalId(userJson),
          email: user.mail,
          tam_id: user.saml_tam_id,
          life_stage: user.saml_life_stage,
          member_status: user.saml_member_status,
          roles: this.parseUserRoles(user.roles),
          name: user.field_first_name + ' ' + user.field_last_name,
          savedSearches: this.parseSavedSearches(user.field_saved_searches || []),
          savedPrograms: this.parseDashboardPrograms(user.field_dashboard_programs_ref.field_udp_dashboard_array || [], user.field_aci_dashboard_array),
          programDownloads: user.field_program_download_count
        })
    );
  }

  public static parseUserNotes(userJson: any): UserModel[] {
    const userFields = [
      'id',
      'mail',
      'name',
      'roles.label',
      'field_saved_searches',
      'field_program_download_count',
      'saml_tam_id',
      'saml_life_stage',
      'saml_member_status',
      'field_dashboard_programs_ref.field_udp_dashboard_array.field_specialty.title',
      'field_dashboard_programs_ref.field_udp_dashboard_array.field_specialty.drupal_internal__nid',
      'field_dashboard_programs_ref.field_udp_dashboard_array.field_specialty.field_specialty_designation',
      'field_dashboard_programs_ref.field_udp_dashboard_array.path'
    ].concat(UserServiceHelper.DASHBOARD_PROGRAMS_FIELDS);


    let normdUsers = normalize(userJson).get(userFields);
    if (!Array.isArray(normdUsers)) {
      normdUsers = [normdUsers];
    }

    return normdUsers[0].field_dashboard_programs_ref.field_user_notes ? this.parseNotes(normdUsers[0].field_dashboard_programs_ref.field_user_notes) : [];
  }


  public static parseSavedSearches(savedSearches: any[]) {
    return savedSearches.length === 0
      ? ([] as SavedSearchModel[])
      : savedSearches.map(
        (savedSearch: any) =>
          new SavedSearchModel({
            name: savedSearch.name,
            querystring: savedSearch.query_string,
            resultCount: savedSearch.results,
            query_label: savedSearch.query_label
          })
      );
  }

  public static parseDashboardPrograms(dashboardPrograms: any, aciSurveyDashboard) {
    const trimmedFields = UserServiceHelper.DASHBOARD_PROGRAMS_FIELDS.map((f) =>
      f.substring(f.indexOf('.') + 1)
    );
    const formattedDashboardPrograms = SearchServiceHelper.parsePrograms(
      dashboardPrograms,
      trimmedFields,
      true,
      aciSurveyDashboard
    );

    return formattedDashboardPrograms.filter(
      (dp) => dp.uuid !== null && dp.uuid !== undefined && dp.uuid !== ''
    );
  }

  public static parseAciSurveyResults(aciSurveyPrograms: []) {
    const temp_arr = [];
    aciSurveyPrograms.map( (result: string) => {
      const pgm = result.split(',');
      temp_arr.push( {
        'program_number': pgm[0],
        'aci_score': pgm[1]
      });
    });
    return temp_arr;
  }

  public static parseUserRoles(userRoles: string[]) {
    // try to get roles from cookie first, but if cookie isn't there, use userRoles param
    const getJwtCookie = (c: string) => {
      const key = c.split('=')[0];
      if (key) {
        return key === environment.jwtCookieName;
      }

      return false;
    };

    // check cookie for roles
    let tokenCookies: any;
    if (document) {
      const cookie = document.cookie.split(';').map((c: string) => c.trim());
      tokenCookies = cookie.filter(getJwtCookie);
    }

    if (tokenCookies && tokenCookies.length > 0) {
      const tokenCookie = tokenCookies[0];
      const token = tokenCookie.split('=')[1];
      const decoded = this.decodeToken(token);

      return decoded.drupal.roles as string[];
    } else {
      return userRoles.length > 0 ? userRoles.map((role: any) => role.label as string) : [];
    }
  }

  public static decodeToken(token: string): any {
    return jwtDecode(token) as any;
  }

  public static getUdpInternalId(userJson: any) {
    const  uuid = userJson.included.filter( (node) => {
      if (node['type'] === 'node--users_dashboard_programs') {
        return node.id;
      }
    })
    return uuid[0].id;
  }

  private static parseNotes(notes) {
    let tmpNotes: [];
    console.log('Notes: ', notes);
     notes.map( (note) => {
       if (!note.field_program) {
         return null;
       }
      note =  new NoteModel({
        uuid: note.id,
        programUuid: note.field_program.id,
        communityType: note.field_program_community,
        costOfLiving: note.field_program_col,
        culturePersonalityFit: note.field_program_fit,
        facultyTeachingAvailability: note.field_fac_teaching_avail,
        interviewDateTime: note.field_program_interview,
        matchRankOrder: note.field_match_rank_order,
        programNumber: note.field_program.field_program_id,
        rating: note.field_program_rating,
        residentsMorale: note.field_program_morale,
        status: note.field_program_status,
        programName: note.field_program.title,
        specialtyName: note.field_program.field_specialty.title
      });
    note.comments = note.field_program_comments ?
      note.field_program_comments.map((c: any) => new CommentModel({ comment: c.comment, commentTimeStamp: c.value }))
      : [];
  })
    return notes;
}}
